import React, { useEffect, useState, useMemo } from "react";
import { api, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUSES = ["To Do", "In Progress", "Blocked", "Completed"];
const PRIORITIES = ["Low", "Medium", "High", "Urgent"];
const DEPARTMENTS = ["Operations", "Business Development", "Marketing", "Finance", "Events", "Management"];

const TaskDialog = ({ users, venues, events, onSaved, initial, trigger, defaultAssigneeId }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial || {
    title: "", description: "", assignee_id: defaultAssigneeId || "", department: "Operations",
    priority: "Medium", status: "To Do", due_date: "",
    venue_id: "", event_id: "", tags: [],
  });

  useEffect(() => { if (initial) setForm(initial); }, [initial]);
  useEffect(() => {
    if (!initial && defaultAssigneeId && !form.assignee_id) {
      setForm((f) => ({ ...f, assignee_id: defaultAssigneeId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAssigneeId]);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    const assignee = users.find((u) => u.id === form.assignee_id);
    const venue = venues.find((v) => v.id === form.venue_id);
    const event = events.find((e) => e.id === form.event_id);
    const payload = {
      ...form,
      assignee_name: assignee?.name || "",
      venue_name: venue?.name || null,
      event_name: event?.name || null,
      venue_id: form.venue_id || null,
      event_id: form.event_id || null,
      link_type: form.event_id ? "event" : form.venue_id ? "venue" : null,
      link_id: form.event_id || form.venue_id || null,
    };
    try {
      if (initial?.id) {
        await api.patch(`/tasks/${initial.id}`, payload);
        toast.success("Task updated");
      } else {
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error("Failed to save task");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit Task" : "New Task"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Title</Label>
            <Input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>Description</Label>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <Label>Assignee</Label>
            <Select value={form.assignee_id || ""} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
              <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Select user" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Department</Label>
            <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Due Date</Label>
            <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
          </div>
          <div>
            <Label>Link to Venue</Label>
            <Select value={form.venue_id || "none"} onValueChange={(v) => setForm({ ...form, venue_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {venues.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Link to Event</Label>
            <Select value={form.event_id || "none"} onValueChange={(v) => setForm({ ...form, event_id: v === "none" ? "" : v })}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid="task-save-button" onClick={save} className="bg-blue-600 hover:bg-blue-700">Save Task</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Tasks({ scope: initialScope = "all" }) {
  const { user } = useAuth();
  const [scope, setScope] = useState(initialScope);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [venues, setVenues] = useState([]);
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState({ status: "all", priority: "all", assignee: "all" });

  const load = async () => {
    const [t, u, v, e] = await Promise.all([
      api.get("/tasks"), api.get("/users"), api.get("/venues"), api.get("/events"),
    ]);
    setTasks(t.data); setUsers(u.data); setVenues(v.data); setEvents(e.data);
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let arr = tasks;
    if (scope === "mine") arr = arr.filter((t) => t.assignee_id === user?.id);
    if (filter.status !== "all") arr = arr.filter((t) => t.status === filter.status);
    if (filter.priority !== "all") arr = arr.filter((t) => t.priority === filter.priority);
    if (filter.assignee !== "all") arr = arr.filter((t) => t.assignee_id === filter.assignee);
    return arr;
  }, [tasks, filter, scope, user]);

  const del = async (id) => {
    await api.delete(`/tasks/${id}`);
    toast.success("Task deleted");
    load();
  };

  const changeStatus = async (t, status) => {
    await api.patch(`/tasks/${t.id}`, { status, completed_date: status === "Completed" ? new Date().toISOString() : null });
    load();
  };

  return (
    <div className="space-y-6" data-testid="tasks-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Tasks</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {scope === "mine" ? "My Tasks" : "All Tasks"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 bg-white p-1">
            <button
              data-testid="scope-mine"
              onClick={() => setScope("mine")}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors duration-200 ${scope === "mine" ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            >Mine</button>
            <button
              data-testid="scope-all"
              onClick={() => setScope("all")}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors duration-200 ${scope === "all" ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-slate-100"}`}
            >All</button>
          </div>
          <TaskDialog
            users={users} venues={venues} events={events} onSaved={load}
            defaultAssigneeId={user?.id}
            trigger={<Button data-testid="new-task-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> New Task</Button>}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
          <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.priority} onValueChange={(v) => setFilter({ ...filter, priority: v })}>
          <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filter.assignee} onValueChange={(v) => setFilter({ ...filter, assignee: v })}>
          <SelectTrigger><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All assignees</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="kanban">
        <TabsList>
          <TabsTrigger data-testid="tab-kanban" value="kanban">Kanban</TabsTrigger>
          <TabsTrigger data-testid="tab-table" value="table">Table</TabsTrigger>
        </TabsList>
        <TabsContent value="kanban">
          <div className="flex gap-4 overflow-x-auto pb-4">
            {STATUSES.map((s) => (
              <div key={s} className="min-w-[280px] flex-1 bg-slate-100 rounded-md p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-widest text-slate-600 font-bold">{s}</div>
                  <span className="text-xs text-slate-500">{filtered.filter((t) => t.status === s).length}</span>
                </div>
                <div className="space-y-2">
                  {filtered.filter((t) => t.status === s).map((t) => (
                    <Card key={t.id} data-testid={`task-card-${t.id}`} className="border-slate-200 bg-white">
                      <CardContent className="p-3">
                        <div className="flex justify-between items-start gap-2">
                          <div className="text-sm font-semibold text-slate-900">{t.title}</div>
                          <StatusBadge status={t.priority} />
                        </div>
                        {t.description && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>}
                        <div className="text-xs text-slate-500 mt-2 flex justify-between items-center">
                          <span>{t.assignee_name}</span>
                          <span>{fmtDate(t.due_date)}</span>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Select value={t.status} onValueChange={(v) => changeStatus(t, v)}>
                            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s2) => <SelectItem key={s2} value={s2}>{s2}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <TaskDialog
                            users={users} venues={venues} events={events} onSaved={load} initial={t}
                            trigger={<Button variant="outline" size="sm" className="h-7 text-xs">Edit</Button>}
                          />
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
        <TabsContent value="table">
          <Card className="border-slate-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No tasks match your filters.</TableCell></TableRow>
                )}
                {filtered.map((t) => (
                  <TableRow key={t.id} data-testid={`task-row-${t.id}`} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell>{t.assignee_name}</TableCell>
                    <TableCell>{t.department}</TableCell>
                    <TableCell><StatusBadge status={t.priority} /></TableCell>
                    <TableCell><StatusBadge status={t.status} /></TableCell>
                    <TableCell>{fmtDate(t.due_date)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <TaskDialog users={users} venues={venues} events={events} onSaved={load} initial={t}
                          trigger={<Button variant="ghost" size="sm">Edit</Button>} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete task?</AlertDialogTitle>
                              <AlertDialogDescription>This can't be undone.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => del(t.id)}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
